/**
 * TreatmentModal — risk treatment decision (Avoid / Reduce / Share / Retain).
 *
 * Rebuilt on the shared FormModal primitive. The old bespoke 3-step wizard
 * (selection → claim | reduce) flattens into ONE declarative form: the
 * treatment radio is always visible at the top and each branch's fields
 * reveal through per-field `visible()` rules — picking Share/Retain shows
 * the claim section (existing-vs-new mode radio, claim cards or the new-claim
 * form), picking Reduce shows the reduce section (requirement-vs-control mode
 * radio + that mode's fields), picking Avoid needs nothing further. FormModal
 * owns state, per-branch required validation (hidden fields are skipped),
 * the submit lifecycle, and inline submit errors.
 *
 * Submit payload shapes (unchanged — consumed by actions.submitDecision):
 *   Avoid                → { treatment: 'Avoid' }
 *   Share/Retain + link  → { treatment, existing_claim_id }
 *   Share/Retain + new   → { treatment, claim_id, claim_statement,
 *                            justification, review_date? }
 *   Reduce + requirement → { treatment: 'Reduce/NewRequirement', control_id,
 *                            new_requirement_statement,
 *                            new_requirement_rationale? }
 *   Reduce + control     → { treatment: 'Reduce/NewControl', goal_id,
 *                            control_id?, control_name, control_description,
 *                            requirements: [...] }
 *
 * Editing an already-decided risk pre-fills via `initialValues` (provisional
 * claim / control / requirement from the risk doc) — the visible() rules then
 * reveal the right branch automatically, replacing the old step-jumping.
 */

import { useMemo, useState } from 'react';
import FormModal from '../../../components/ui/FormModal/FormModal';
import styles from './TreatmentModal.module.css';

const TREATMENT_OPTIONS = [
  {
    value: 'Avoid',
    label: 'Avoid',
    description: 'Eliminate risk by removing feature or changing design. No claim required.',
  },
  {
    value: 'Reduce',
    label: 'Reduce',
    description: 'Add controls/requirements to lower risk level. Returns to Step 10 for implementation.',
  },
  {
    value: 'Share',
    label: 'Share',
    description: 'Transfer risk to another party (supplier, insurance). Requires claim (new or existing).',
  },
  {
    value: 'Retain',
    label: 'Retain',
    description: 'Accept residual risk with documented justification. Requires claim (new or existing).',
  },
];

const isClaimTreatment = (t) => t === 'Share' || t === 'Retain';

/** Suggested control_id in format CTRL-{GOAL_ID}-{timestamp}. */
const suggestControlId = (goalId) => {
  if (!goalId) return '';
  const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
  return `CTRL-${goalId}-${ts}`;
};

const SUBMIT_LABELS = {
  Avoid: 'Confirm Avoid',
  Reduce: 'Add & Continue',
};

const TreatmentModal = ({
  isOpen,
  onClose,
  risk,
  controls = [],
  provisionalControls = [],
  goals = [],
  existingClaims = [],
  onSubmit,
}) => {
  // Merge real controls + provisional controls from other risks' decisions
  const allControls = useMemo(
    () => [...controls, ...provisionalControls],
    [controls, provisionalControls],
  );

  // Suggested claim_id in format CLAIM-{ASSET_ID}-{NO}
  const suggestedClaimId = useMemo(() => {
    if (!risk) return '';
    const assetId = risk.asset_id || 'UNKNOWN';
    const assetClaims = existingClaims.filter(
      (c) => c.claim_id && c.claim_id.startsWith(`CLAIM-${assetId}-`),
    );
    const numbers = assetClaims.map((c) => {
      const match = c.claim_id.match(/CLAIM-.*?-(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    });
    const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    return `CLAIM-${assetId}-${String(nextNumber).padStart(2, '0')}`;
  }, [risk, existingClaims]);

  // Pre-fill from the risk's provisional decision (editing an already-
  // decided risk). The visible() rules reveal the matching branch.
  const initialValues = useMemo(() => {
    const out = {
      treatment: '',
      claim_mode: 'new',
      claim_id: suggestedClaimId,
      reduce_mode: 'add_requirement',
    };
    if (!risk?.current_treatment) return out;

    const known = TREATMENT_OPTIONS.some((o) => o.value === risk.current_treatment);
    if (known) out.treatment = risk.current_treatment;

    if (risk.current_treatment === 'Reduce') {
      if (risk.new_control) {
        out.reduce_mode = 'add_control';
        out.goal_id = risk.new_control.goal_id || '';
        out.new_control_id = risk.new_control.control_id || '';
        out.new_control_name = risk.new_control.control_name || '';
        out.new_control_description = risk.new_control.control_description || '';
        const req = risk.new_control.requirements?.[0];
        if (req) {
          out.new_requirement_statement = req.req_statement || '';
          out.new_requirement_rationale = req.req_rationale || '';
        }
      } else if (risk.new_requirement) {
        out.reduce_mode = 'add_requirement';
        out.existing_control_id = risk.new_requirement.control_id || '';
        out.new_requirement_statement =
          risk.new_requirement.requirement?.req_statement || '';
        out.new_requirement_rationale =
          risk.new_requirement.requirement?.req_rationale || '';
      }
    } else if (risk.new_claim) {
      out.claim_id = risk.new_claim.claim_id || suggestedClaimId;
      out.claim_statement = risk.new_claim.claim_statement || '';
      out.justification = risk.new_claim.justification || '';
    }
    return out;
  }, [risk, suggestedClaimId]);

  // Latest form values, mirrored out via onValuesChange so the submit
  // label can follow the selected branch.
  const [liveValues, setLiveValues] = useState(null);
  const live = liveValues ?? initialValues;
  const submitLabel = !live.treatment
    ? 'Submit Decision'
    : SUBMIT_LABELS[live.treatment] ??
      (live.claim_mode === 'existing' ? 'Link Claim' : 'Submit Claim');

  const today = new Date().toISOString().split('T')[0];

  const fields = useMemo(
    () => [
      {
        name: 'treatment',
        type: 'radio',
        label: 'Treatment Option',
        required: true,
        helpText: risk?.current_treatment_decision
          ? `Athena Suggestion: ${risk.current_treatment_decision}`
          : undefined,
        options: TREATMENT_OPTIONS,
      },

      /* ---- Share / Retain → claim section ---- */
      {
        name: 'claim_mode',
        type: 'radio',
        label: 'Claim',
        direction: 'horizontal',
        visible: (v) => isClaimTreatment(v.treatment),
        options: [
          ...(existingClaims.length > 0
            ? [{ value: 'existing', label: `Use existing claim (${existingClaims.length})` }]
            : []),
          { value: 'new', label: 'Create new claim' },
        ],
      },
      {
        name: 'existing_claim_id',
        type: 'radio',
        label: 'Select Claim',
        required: true,
        visible: (v) => isClaimTreatment(v.treatment) && v.claim_mode === 'existing',
        options: existingClaims.map((claim) => ({
          value: claim.claim_id,
          label: claim.claim_id,
          description: (
            <span className={styles.claimOptionBody}>
              <span>{claim.claim_statement}</span>
              {claim.justification && (
                <span>
                  <strong>Justification:</strong> {claim.justification}
                </span>
              )}
              <span className={styles.claimOptionRisks}>
                Linked to: {claim.risk_ids?.join(', ') || 'None'}
              </span>
            </span>
          ),
        })),
      },
      {
        name: 'claim_id',
        type: 'text',
        label: 'Claim ID',
        visible: (v) => isClaimTreatment(v.treatment) && v.claim_mode === 'new',
      },
      {
        name: 'claim_statement',
        type: 'textarea',
        label: 'Claim Statement',
        required: true,
        rows: 3,
        placeholder:
          'e.g., The residual risk is accepted as implemented controls reduce impact to acceptable level.',
        visible: (v) => isClaimTreatment(v.treatment) && v.claim_mode === 'new',
      },
      {
        name: 'justification',
        type: 'textarea',
        label: 'Justification',
        required: true,
        rows: 4,
        placeholder: 'Provide detailed justification for this treatment decision...',
        visible: (v) => isClaimTreatment(v.treatment) && v.claim_mode === 'new',
      },
      {
        name: 'review_date',
        type: 'date',
        label: 'Review Date',
        min: today,
        helpText: 'When should this decision be reviewed?',
        visible: (v) => isClaimTreatment(v.treatment) && v.claim_mode === 'new',
      },

      /* ---- Reduce section ---- */
      {
        name: 'reduce_mode',
        type: 'radio',
        label: 'How do you want to reduce this risk?',
        visible: (v) => v.treatment === 'Reduce',
        options: [
          { value: 'add_requirement', label: 'Add requirement to existing control' },
          { value: 'add_control', label: 'Add new control' },
        ],
      },
      {
        name: 'existing_control_id',
        type: 'select',
        label: 'Select Control',
        required: true,
        placeholder: '-- Select a control --',
        helpText: "Includes controls from other risks' provisional decisions",
        visible: (v) => v.treatment === 'Reduce' && v.reduce_mode === 'add_requirement',
        options: allControls.map((ctrl) => ({
          value: ctrl.control_id,
          label: `${ctrl.control_id}: ${ctrl.control_name}`,
        })),
      },
      {
        name: 'goal_id',
        type: 'select',
        label: 'Select Goal',
        required: true,
        placeholder: '-- Select a goal --',
        visible: (v) => v.treatment === 'Reduce' && v.reduce_mode === 'add_control',
        options: goals.map((goal) => {
          const statement = goal.goal_statement || '';
          return {
            value: goal.goal_id,
            label: `${goal.goal_id}: ${statement.slice(0, 80)}${statement.length > 80 ? '...' : ''}`,
          };
        }),
        // Auto-suggest the control id whenever the goal changes (matches
        // the old modal's suggestion effect).
        afterChange: (goalId, { setValue }) => {
          setValue('new_control_id', suggestControlId(goalId));
        },
      },
      {
        name: 'new_control_id',
        type: 'text',
        label: 'Control ID',
        visible: (v) => v.treatment === 'Reduce' && v.reduce_mode === 'add_control',
      },
      {
        name: 'new_control_name',
        type: 'text',
        label: 'Control Name',
        required: true,
        placeholder: 'e.g., Input Validation Control',
        visible: (v) => v.treatment === 'Reduce' && v.reduce_mode === 'add_control',
      },
      {
        name: 'new_control_description',
        type: 'textarea',
        label: 'Control Description',
        required: true,
        rows: 3,
        placeholder: 'Describe what this control does...',
        visible: (v) => v.treatment === 'Reduce' && v.reduce_mode === 'add_control',
      },
      // Shared between both reduce modes (same state in the old wizard, so
      // switching modes keeps the entered text).
      {
        name: 'new_requirement_statement',
        type: 'textarea',
        label: 'Requirement Statement',
        required: true,
        rows: 3,
        placeholder: 'SHALL implement...',
        visible: (v) => v.treatment === 'Reduce',
      },
      {
        name: 'new_requirement_rationale',
        type: 'textarea',
        label: 'Requirement Rationale',
        rows: 2,
        placeholder: 'Explain why this requirement is needed...',
        visible: (v) => v.treatment === 'Reduce',
      },
      {
        name: '_reduce_note',
        type: 'custom',
        visible: (v) => v.treatment === 'Reduce',
        render: () => (
          <div className={styles.reduceNote}>
            After adding, you&rsquo;ll return to Step 10 to implement the new
            control/requirement.
          </div>
        ),
      },
    ],
    [risk, existingClaims, allControls, goals, today],
  );

  // Map form values onto the submitDecision payload for the chosen branch.
  // Rejection bubbles to FormModal's inline error and keeps the modal open;
  // on success FormModal calls onClose itself.
  const handleSubmit = async (v) => {
    let payload;
    if (v.treatment === 'Avoid') {
      payload = { treatment: 'Avoid' };
    } else if (isClaimTreatment(v.treatment)) {
      payload =
        v.claim_mode === 'existing'
          ? { treatment: v.treatment, existing_claim_id: v.existing_claim_id }
          : {
              treatment: v.treatment,
              claim_id: v.claim_id,
              claim_statement: v.claim_statement.trim(),
              justification: v.justification.trim(),
              review_date: v.review_date || undefined,
            };
    } else if (v.reduce_mode === 'add_requirement') {
      payload = {
        treatment: 'Reduce/NewRequirement',
        control_id: v.existing_control_id,
        new_requirement_statement: v.new_requirement_statement.trim(),
        new_requirement_rationale: v.new_requirement_rationale?.trim() || undefined,
      };
    } else {
      payload = {
        treatment: 'Reduce/NewControl',
        goal_id: v.goal_id,
        control_id: v.new_control_id?.trim() || undefined,
        control_name: v.new_control_name.trim(),
        control_description: v.new_control_description.trim(),
        requirements: v.new_requirement_statement.trim()
          ? [
              {
                req_statement: v.new_requirement_statement.trim(),
                req_rationale: v.new_requirement_rationale?.trim() || undefined,
              },
            ]
          : [],
      };
    }
    await onSubmit(payload);
  };

  if (!isOpen || !risk) return null;

  return (
    <FormModal
      title="Risk Treatment Decision"
      headerExtra={
        <div className={styles.riskInfo}>
          <div className={styles.riskId}>{risk.risk_id}</div>
          <div className={styles.riskName}>
            {risk.asset_name} - {risk.threat_name}
          </div>
          <div className={styles.riskLevel}>
            Current Level: <strong>{risk.current_risk_level}</strong> (
            {risk.current_risk_rating})
          </div>
        </div>
      }
      fields={fields}
      initialValues={initialValues}
      submitLabel={submitLabel}
      submittingLabel="Submitting..."
      onSubmit={handleSubmit}
      onValuesChange={setLiveValues}
      onClose={onClose}
      maxWidth={680}
    />
  );
};

export default TreatmentModal;
